import React, { useState, useEffect } from 'react';
import { getFirestore, collection, onSnapshot, query, where, orderBy, doc, updateDoc, getDocs, getDoc } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { decryptBankAccount } from './utils/encryptionUtils';

const db   = getFirestore();
const auth = getAuth();
const ADMIN_UID = "3wrmp7PRwLenKweORCEKkokpd0u1";

const AdminDashboard = () => {
  const [user, setUser]                               = useState(null);
  const [authLoading, setAuthLoading]                 = useState(true);
  const [email, setEmail]                             = useState('');
  const [password, setPassword]                       = useState('');
  const [loginError, setLoginError]                   = useState('');
  const [loggingIn, setLoggingIn]                     = useState(false);
  const [selectedTab, setSelectedTab]                 = useState('overview');
  const [withdrawals, setWithdrawals]                 = useState([]);
  const [affiliates, setAffiliates]                   = useState([]);
  const [ratingLinks, setRatingLinks]                 = useState([]);
  const [loading, setLoading]                         = useState(true);
  const [selectedWithdrawals, setSelectedWithdrawals] = useState([]);
  const [processingAction, setProcessingAction]       = useState(false);
  const [decryptionErrors, setDecryptionErrors]       = useState(new Set());
  const [bonusUnlockCounts, setBonusUnlockCounts]     = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.uid !== ADMIN_UID) { signOut(auth); setLoginError('Access denied.'); setUser(null); }
      else setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const unsubWithdrawals = onSnapshot(query(collection(db, 'withdrawals'), orderBy('requestedAt', 'desc')), (snap) => {
      setWithdrawals(snap.docs.map(d => ({ id: d.id, ...d.data(), requestedAt: d.data().requestedAt?.toDate() || new Date(), processedAt: d.data().processedAt?.toDate() || null })));
      setLoading(false);
    });
    const unsubAffiliates = onSnapshot(collection(db, 'affiliates'), (snap) => {
      setAffiliates(snap.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate() || new Date() })));
    });
    const unsubLinks = onSnapshot(query(collection(db, 'rating_links'), orderBy('createdAt', 'desc')), (snap) => {
      setRatingLinks(snap.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate() || new Date(), expiresAt: d.data().expiresAt?.toDate() || null })));
    });
    return () => { unsubWithdrawals(); unsubAffiliates(); unsubLinks(); };
  }, [user]);

  // Fetch bonus unlock counts per link
  useEffect(() => {
    if (!user || ratingLinks.length === 0) return;
    const fetchUnlockCounts = async () => {
      const chunkSize = 10;
      for (let i = 0; i < ratingLinks.length; i += chunkSize) {
        const chunk = ratingLinks.slice(i, i + chunkSize);
        const entries = await Promise.all(
          chunk.map(async (link) => {
            const q = query(collection(db, 'bonus_unlocks'), where('linkDocId', '==', link.id));
            const snap = await getDocs(q);
            return [link.id, snap.size];
          })
        );
        setBonusUnlockCounts(prev => ({ ...prev, ...Object.fromEntries(entries) }));
      }
    };
    fetchUnlockCounts();
  }, [user, ratingLinks]);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoginError(''); setLoggingIn(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      if (cred.user.uid !== ADMIN_UID) { await signOut(auth); setLoginError('Access denied.'); }
    } catch (err) { setLoginError(err.message || 'Failed to log in.'); }
    finally { setLoggingIn(false); }
  };

  const handleLogout = async () => { try { await signOut(auth); } catch {} };

  const updateWithdrawalStatus = async (withdrawalId, newStatus, rejectionReason = null) => {
    setProcessingAction(true);
    try {
      const updateData = { status: newStatus, processedAt: new Date() };
      if (rejectionReason) updateData.rejectionReason = rejectionReason;
      await updateDoc(doc(db, 'withdrawals', withdrawalId), updateData);
    } catch (err) { alert('Error: ' + err.message); }
    setProcessingAction(false);
  };

  const bulkUpdateWithdrawals = async (ids, newStatus) => {
    setProcessingAction(true);
    try {
      await Promise.all(ids.map(id => updateDoc(doc(db, 'withdrawals', id), { status: newStatus, processedAt: new Date() })));
      setSelectedWithdrawals([]);
    } catch (err) { alert('Error: ' + err.message); }
    setProcessingAction(false);
  };

  const getDecryptedBankAccount = async (withdrawal) => {
    try {
      if (withdrawal.encryptedBankAccount) return await decryptBankAccount(withdrawal.encryptedBankAccount);
      if (withdrawal.bankAccount) return withdrawal.bankAccount;
      throw new Error('No bank account data found');
    } catch (err) {
      setDecryptionErrors(prev => new Set([...prev, withdrawal.id]));
      return { accountHolderName: '[Decryption Failed]', bankName: '[Decryption Failed]', accountNumber: '0000', routingNumber: '000000000', accountType: 'checking', addressLine1: '[Decryption Failed]', city: '[Decryption Failed]', state: 'XX', zipCode: '00000' };
    }
  };

  const generateWiseCSV = async () => {
    const approved = withdrawals.filter(w => w.status === 'approved');
    if (approved.length === 0) { alert('No approved withdrawals to export'); return; }
    setProcessingAction(true);
    try {
      const wiseCSVData = []; const failed = []; const succeeded = [];
      for (const w of approved) {
        const affiliate = affiliates.find(a => a.id === w.userId);
        if (!affiliate) continue;
        try {
          const bank = await getDecryptedBankAccount(w);
          if (bank.accountHolderName === '[Decryption Failed]') { failed.push(w.id); continue; }
          if (!bank.addressLine1 || !bank.city || !bank.state || !bank.zipCode) continue;
          wiseCSVData.push({ sourceCurrency: 'GBP', targetCurrency: 'USD', amount: w.amount.toFixed(2), amountCurrency: 'target', name: bank.accountHolderName, email: affiliate.email, reference: `SocialStar earnings ${new Date().toISOString().split('T')[0]}`, receiverType: 'PERSON', accountNumber: bank.accountNumber, abartn: bank.routingNumber, accountType: bank.accountType === 'checking' ? 'CHECKING' : 'SAVINGS', addressFirstLine: bank.addressLine1, addressCity: bank.city, addressState: bank.state, addressPostCode: bank.zipCode, addressCountryCode: 'US' });
          succeeded.push(w.id);
        } catch { failed.push(w.id); }
      }
      if (failed.length > 0) alert(`Warning: ${failed.length} could not be decrypted. Succeeded: ${succeeded.length}`);
      if (wiseCSVData.length === 0) { alert('No valid withdrawals to export'); return; }
      const cols = ['sourceCurrency','targetCurrency','amount','amountCurrency','name','email','reference','receiverType','accountNumber','abartn','accountType','addressFirstLine','addressCity','addressState','addressPostCode','addressCountryCode'];
      let csv = cols.join(',') + '\n';
      wiseCSVData.forEach(row => { csv += cols.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(',') + '\n'; });
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `wise_payouts_${new Date().toISOString().split('T')[0]}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      if (failed.length === 0) alert(`Generated CSV with ${wiseCSVData.length} withdrawals!`);
    } catch (err) { alert('Error: ' + err.message); }
    finally { setProcessingAction(false); }
  };

  const markBatchCompleted = async () => {
    const approved = withdrawals.filter(w => w.status === 'approved');
    if (approved.length === 0) { alert('No approved withdrawals'); return; }
    if (!window.confirm(`Mark ${approved.length} withdrawals as completed?`)) return;
    await bulkUpdateWithdrawals(approved.map(w => w.id), 'completed');
  };

  const stats = {
    pendingCount:    withdrawals.filter(w => w.status === 'pending').length,
    pendingAmount:   withdrawals.filter(w => w.status === 'pending').reduce((s, w) => s + w.amount, 0),
    approvedCount:   withdrawals.filter(w => w.status === 'approved').length,
    approvedAmount:  withdrawals.filter(w => w.status === 'approved').reduce((s, w) => s + w.amount, 0),
    todayRequests:   withdrawals.filter(w => w.requestedAt.toDateString() === new Date().toDateString()).length,
    totalAffiliates: affiliates.length,
    totalLinks:      ratingLinks.length,
    activeLinks:     ratingLinks.filter(l => !l.expiresAt || l.expiresAt > new Date()).length,
    totalPurchases:  Object.values(bonusUnlockCounts).reduce((s, n) => s + n, 0)
  };

  // ── Pricing control ───────────────────────────────────────
  const AffiliatePricingControl = () => {
    const [earningsPerRating, setEarningsPerRating] = useState('');
    const [currentValue, setCurrentValue]           = useState(null);
    const [updating, setUpdating]                   = useState(false);
    const [message, setMessage]                     = useState(null);

    useEffect(() => {
      const unsub = onSnapshot(doc(db, 'app_config', 'affiliate_pricing'), (d) => {
        if (d.exists()) {
          const val = d.data().earnings_per_rating ?? 0.25;
          setCurrentValue(val); setEarningsPerRating(val.toString());
        }
      });
      return () => unsub();
    }, []);

    const updatePricing = async () => {
      const value = parseFloat(earningsPerRating);
      if (isNaN(value) || value < 0 || value > 10) { setMessage({ type: 'error', text: 'Enter a value between $0.00 and $10.00' }); return; }
      if (!window.confirm(`Change earnings per rating from $${currentValue?.toFixed(2)} to $${value.toFixed(2)}?`)) return;
      setUpdating(true); setMessage(null);
      try {
        await updateDoc(doc(db, 'app_config', 'affiliate_pricing'), { earnings_per_rating: value, updated_at: new Date(), updated_by: 'admin' });
        setMessage({ type: 'success', text: `Updated to $${value.toFixed(2)} per rating.` });
      } catch (err) { setMessage({ type: 'error', text: `Error: ${err.message}` }); }
      finally { setUpdating(false); }
    };

    return (
      <div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#495057' }}>Current Earnings Per Rating</label>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#28a745', marginBottom: '20px' }}>${currentValue?.toFixed(2) || '0.25'}</div>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#495057' }}>New Earnings Per Rating ($)</label>
          <input type="number" step="0.01" min="0" max="10" value={earningsPerRating} onChange={e => setEarningsPerRating(e.target.value)} placeholder="0.25"
            style={{ width: '200px', padding: '10px', fontSize: '16px', border: '2px solid #dee2e6', borderRadius: '6px', marginRight: '10px' }} />
          <button onClick={updatePricing} disabled={updating || !earningsPerRating}
            style={{ padding: '10px 20px', backgroundColor: updating ? '#6c757d' : '#007bff', color: 'white', border: 'none', borderRadius: '6px', cursor: updating || !earningsPerRating ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
            {updating ? '⏳ Updating...' : '💾 Update Pricing'}
          </button>
        </div>
        {message && <div style={{ padding: '12px', borderRadius: '6px', backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da', color: message.type === 'success' ? '#155724' : '#721c24', fontSize: '14px' }}>{message.text}</div>}
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '6px', fontSize: '13px', color: '#004085' }}>
          <strong>💡 Common Values:</strong>
          <div style={{ marginTop: '8px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {[0.05, 0.10, 0.25, 0.50, 1.00].map(val => (
              <button key={val} onClick={() => setEarningsPerRating(val.toString())}
                style={{ padding: '6px 12px', backgroundColor: 'white', border: '1px solid #007bff', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#007bff' }}>
                ${val.toFixed(2)}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (authLoading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '18px' }}>Loading...</div>;

  if (!user) return (
    <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8f9fa', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px' }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '24px', fontWeight: 'bold', textAlign: 'center' }}>SocialStar Admin</h1>
        <p style={{ margin: '0 0 30px 0', fontSize: '14px', color: '#6c757d', textAlign: 'center' }}>Sign in to access the dashboard</p>
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#495057' }}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" style={{ width: '100%', padding: '12px', fontSize: '14px', border: '1px solid #dee2e6', borderRadius: '6px', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#495057' }}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" style={{ width: '100%', padding: '12px', fontSize: '14px', border: '1px solid #dee2e6', borderRadius: '6px', boxSizing: 'border-box' }} />
          </div>
          {loginError && <div style={{ padding: '12px', marginBottom: '20px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '6px', color: '#721c24', fontSize: '14px' }}>{loginError}</div>}
          <button type="submit" disabled={loggingIn} style={{ width: '100%', padding: '12px', backgroundColor: loggingIn ? '#6c757d' : '#007bff', color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', fontWeight: 'bold', cursor: loggingIn ? 'not-allowed' : 'pointer' }}>
            {loggingIn ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <header style={{ backgroundColor: '#2c3e50', color: 'white', padding: '20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>SocialStar Admin Dashboard</h1>
            <p style={{ margin: '5px 0 0', opacity: 0.8, fontSize: '14px' }}>Pay-per-rating · Bonus photo unlocks · 🔒 Bank details encrypted</p>
          </div>
          <button onClick={handleLogout} style={{ padding: '8px 16px', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>Logout</button>
        </div>
      </header>

      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #dee2e6' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex' }}>
          {[{ id: 'overview', label: '📊 Overview' }, { id: 'links', label: '🔗 Links' }, { id: 'withdrawals', label: '💸 Withdrawals' }, { id: 'affiliates', label: '👥 Affiliates' }, { id: 'analytics', label: '📈 Analytics' }].map(tab => (
            <button key={tab.id} onClick={() => setSelectedTab(tab.id)}
              style={{ padding: '15px 25px', border: 'none', backgroundColor: selectedTab === tab.id ? '#007bff' : 'transparent', color: selectedTab === tab.id ? 'white' : '#495057', cursor: 'pointer', fontSize: '14px', fontWeight: selectedTab === tab.id ? 'bold' : 'normal' }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px' }}>

        {/* ── OVERVIEW ── */}
        {selectedTab === 'overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              {[
                { label: 'Pending Review',    value: stats.pendingCount,    sub: `$${stats.pendingAmount.toFixed(2)} total`,    color: '#dc3545' },
                { label: 'Ready for Batch',   value: stats.approvedCount,   sub: `$${stats.approvedAmount.toFixed(2)} total`,   color: '#28a745' },
                { label: "Today's Requests",  value: stats.todayRequests,   sub: 'requests submitted',                          color: '#007bff' },
                { label: 'Total Affiliates',  value: stats.totalAffiliates, sub: 'active users',                                color: '#6f42c1' },
                { label: 'Bonus Purchases',   value: stats.totalPurchases,  sub: 'total unlocks',                               color: '#e67e22' },
              ].map(card => (
                <div key={card.label} style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                  <h3 style={{ margin: '0 0 10px', color: '#495057', fontSize: '16px' }}>{card.label}</h3>
                  <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: card.color }}>{card.value}</p>
                  <p style={{ margin: '5px 0 0', fontSize: '14px', color: '#6c757d' }}>{card.sub}</p>
                </div>
              ))}
            </div>

            {stats.approvedCount > 0 && (
              <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#495057' }}>🚀 Ready for Batch Processing</h3>
                <p style={{ fontWeight: 'bold', color: '#007bff' }}>{stats.approvedCount} withdrawals • ${stats.approvedAmount.toFixed(2)} total</p>
                <div style={{ display: 'flex', gap: '15px', marginTop: '15px' }}>
                  <button onClick={generateWiseCSV} disabled={processingAction} style={{ padding: '12px 25px', backgroundColor: processingAction ? '#6c757d' : '#007bff', color: 'white', border: 'none', borderRadius: '6px', cursor: processingAction ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
                    {processingAction ? '🔓 Decrypting...' : '📥 Download Decrypted CSV'}
                  </button>
                  <button onClick={markBatchCompleted} disabled={processingAction} style={{ padding: '12px 25px', backgroundColor: processingAction ? '#6c757d' : '#28a745', color: 'white', border: 'none', borderRadius: '6px', cursor: processingAction ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
                    ✅ Mark Batch Completed
                  </button>
                </div>
              </div>
            )}

            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginBottom: '20px', color: '#495057' }}>Recent Withdrawals</h3>
              {withdrawals.slice(0, 5).map(w => {
                const affiliate = affiliates.find(a => a.id === w.userId);
                return (
                  <div key={w.id} style={{ padding: '15px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ margin: '0 0 5px', fontWeight: 'bold' }}>{affiliate?.firstName} {affiliate?.lastName} • ${w.amount.toFixed(2)}</p>
                      <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>{w.requestedAt.toLocaleString()}</p>
                    </div>
                    <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', backgroundColor: w.status === 'pending' ? '#fff3cd' : w.status === 'approved' ? '#d1ecf1' : w.status === 'completed' ? '#d4edda' : '#f8d7da', color: w.status === 'pending' ? '#856404' : w.status === 'approved' ? '#0c5460' : w.status === 'completed' ? '#155724' : '#721c24' }}>
                      {w.status}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── LINKS ── */}
        {selectedTab === 'links' && (
          <div>
            <h2 style={{ margin: '0 0 20px', color: '#495057' }}>Rating Links</h2>
            <div style={{ backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              {ratingLinks.map((link) => {
                const affiliate   = affiliates.find(a => a.id === link.affiliateId);
                const isExpired   = link.expiresAt && link.expiresAt < new Date();
                const purchases   = bonusUnlockCounts[link.id] || 0;

                const pageOpens      = link.totalPageOpens      || 0;
                const ratings        = link.totalRatings        || 0;
                const continueClicks = link.totalContinueClicks || 0;
                const unlockClicks   = link.totalUnlockClicks   || 0;

                const pct = (num, denom) => denom > 0 ? ((num / denom) * 100).toFixed(1) + '%' : '—';

                const funnelSteps = [
                  { label: 'Page Opens',  value: pageOpens,      conv: '100%' },
                  { label: 'Rated',       value: ratings,        conv: pct(ratings, pageOpens) },
                  { label: 'Continued',   value: continueClicks, conv: pct(continueClicks, ratings) },
                  { label: 'Tapped Unlock', value: unlockClicks, conv: pct(unlockClicks, continueClicks) },
                  { label: 'Purchased',   value: purchases,      conv: pct(purchases, unlockClicks) },
                ];

                return (
                  <div key={link.id} style={{ padding: '25px', borderBottom: '1px solid #eee' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                      <div>
                        <h4 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 'bold' }}>{affiliate?.firstName} {affiliate?.lastName}</h4>
                        <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#6c757d' }}>{affiliate?.email}</p>
                        <p style={{ margin: 0, fontSize: '12px', color: '#6c757d', fontFamily: 'monospace' }}>
                          {link.linkId} · {link.createdAt.toLocaleDateString()}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <span style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 'bold', backgroundColor: isExpired ? '#f8d7da' : '#d4edda', color: isExpired ? '#721c24' : '#155724' }}>
                          {isExpired ? 'EXPIRED' : 'ACTIVE'}
                        </span>
                        <span style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 'bold', backgroundColor: '#e8f5e9', color: '#2e7d32' }}>
                          ${(link.earnings || 0).toFixed(2)} earned
                        </span>
                        <span style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 'bold', backgroundColor: '#fff3e0', color: '#e65100' }}>
                          {purchases} purchase{purchases !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {/* Headline: Page Opens → Purchased */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
                      <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '600', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Page Opens</p>
                        <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#212529' }}>{pageOpens.toLocaleString()}</p>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                        <span style={{ fontSize: '20px', color: '#adb5bd' }}>›</span>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#6c757d', whiteSpace: 'nowrap' }}>{pct(purchases, pageOpens)}</span>
                      </div>
                      <div style={{ backgroundColor: '#fff3e0', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '600', color: '#e65100', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Purchased</p>
                        <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#e65100' }}>{purchases.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Divider */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                      <span style={{ fontSize: '11px', color: '#adb5bd', whiteSpace: 'nowrap' }}>full funnel</span>
                      <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                    </div>

                    {/* Full funnel breakdown */}
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'stretch', overflowX: 'auto', paddingBottom: '4px' }}>
                      {funnelSteps.map((step, i) => (
                        <React.Fragment key={step.label}>
                          <div style={{ minWidth: '90px', flexShrink: 0, backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                            <p style={{ margin: '0 0 6px', fontSize: '10px', fontWeight: '600', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{step.label}</p>
                            <p style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 'bold', color: '#212529' }}>{step.value.toLocaleString()}</p>
                            <p style={{ margin: 0, fontSize: '11px', color: '#6c757d' }}>{step.conv}</p>
                          </div>
                          {i < funnelSteps.length - 1 && <div style={{ display: 'flex', alignItems: 'center', color: '#adb5bd', fontSize: '16px', flexShrink: 0 }}>›</div>}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })}
              {ratingLinks.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>No links yet</div>}
            </div>
          </div>
        )}

        {/* ── WITHDRAWALS ── */}
        {selectedTab === 'withdrawals' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#495057' }}>Withdrawal Requests</h2>
              {selectedWithdrawals.length > 0 && (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => bulkUpdateWithdrawals(selectedWithdrawals, 'approved')} disabled={processingAction} style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: processingAction ? 'not-allowed' : 'pointer' }}>✅ Bulk Approve ({selectedWithdrawals.length})</button>
                  <button onClick={() => bulkUpdateWithdrawals(selectedWithdrawals, 'rejected')} disabled={processingAction} style={{ padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: processingAction ? 'not-allowed' : 'pointer' }}>❌ Bulk Reject ({selectedWithdrawals.length})</button>
                </div>
              )}
            </div>
            <div style={{ backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              {withdrawals.map(w => {
                const affiliate  = affiliates.find(a => a.id === w.userId);
                const isSelected = selectedWithdrawals.includes(w.id);
                return (
                  <div key={w.id} style={{ padding: '20px', borderBottom: '1px solid #eee', backgroundColor: isSelected ? '#f8f9fa' : 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                      <input type="checkbox" checked={isSelected} onChange={e => setSelectedWithdrawals(e.target.checked ? [...selectedWithdrawals, w.id] : selectedWithdrawals.filter(id => id !== w.id))} style={{ marginTop: '2px' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                          <div>
                            <h4 style={{ margin: '0 0 5px', fontSize: '16px', fontWeight: 'bold' }}>{affiliate?.firstName} {affiliate?.lastName}</h4>
                            <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>{affiliate?.email}</p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#28a745' }}>${w.amount.toFixed(2)}</p>
                            <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#6c757d' }}>{w.requestedAt.toLocaleString()}</p>
                          </div>
                        </div>
                        <div style={{ backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '6px', marginBottom: '15px' }}>
                          <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>🔒 Bank details encrypted • Decrypted during CSV export</p>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ padding: '6px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', backgroundColor: w.status === 'pending' ? '#fff3cd' : w.status === 'approved' ? '#d1ecf1' : w.status === 'completed' ? '#d4edda' : '#f8d7da', color: w.status === 'pending' ? '#856404' : w.status === 'approved' ? '#0c5460' : w.status === 'completed' ? '#155724' : '#721c24' }}>{w.status.toUpperCase()}</span>
                          {w.status === 'pending' && (
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <button onClick={() => updateWithdrawalStatus(w.id, 'approved')} disabled={processingAction} style={{ padding: '6px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: processingAction ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}>✅ APPROVE</button>
                              <button onClick={() => { const r = prompt('Rejection reason (optional):'); updateWithdrawalStatus(w.id, 'rejected', r); }} disabled={processingAction} style={{ padding: '6px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: processingAction ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}>❌ REJECT</button>
                            </div>
                          )}
                          {w.status === 'approved' && <div style={{ padding: '6px 12px', backgroundColor: '#d1ecf1', borderRadius: '4px', fontSize: '12px', color: '#0c5460' }}>Ready for batch</div>}
                        </div>
                        {w.rejectionReason && <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px', fontSize: '12px', color: '#721c24' }}><strong>Reason:</strong> {w.rejectionReason}</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
              {withdrawals.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>No withdrawal requests yet</div>}
            </div>
          </div>
        )}

        {/* ── AFFILIATES ── */}
        {selectedTab === 'affiliates' && (
          <div>
            <h2 style={{ marginBottom: '20px', color: '#495057' }}>All Affiliates</h2>
            <div style={{ backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    {['Name','Email','Balance','Total Earned','Total Ratings','Link Creation','Joined','Actions'].map(h => (
                      <th key={h} style={{ padding: '15px', textAlign: h === 'Name' || h === 'Email' ? 'left' : 'center', borderBottom: '1px solid #dee2e6', fontSize: '13px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {affiliates.map(affiliate => {
                    const canCreateLinks = affiliate.canCreateLinks !== false;
                    return (
                      <tr key={affiliate.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '15px' }}><p style={{ margin: 0, fontWeight: 'bold' }}>{affiliate.firstName} {affiliate.lastName}</p></td>
                        <td style={{ padding: '15px' }}><p style={{ margin: 0, fontSize: '14px' }}>{affiliate.email}</p></td>
                        <td style={{ padding: '15px', textAlign: 'center' }}><strong style={{ color: '#28a745' }}>${(affiliate.balance || 0).toFixed(2)}</strong></td>
                        <td style={{ padding: '15px', textAlign: 'center' }}><span style={{ color: '#6c757d' }}>${(affiliate.totalEarnings || 0).toFixed(2)}</span></td>
                        <td style={{ padding: '15px', textAlign: 'center' }}><span style={{ color: '#007bff', fontWeight: 'bold' }}>{(affiliate.totalRatings || 0).toLocaleString()}</span></td>
                        <td style={{ padding: '15px', textAlign: 'center' }}>
                          <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', backgroundColor: canCreateLinks ? '#d4edda' : '#f8d7da', color: canCreateLinks ? '#155724' : '#721c24' }}>
                            {canCreateLinks ? '✅ Enabled' : '🚫 Blocked'}
                          </span>
                        </td>
                        <td style={{ padding: '15px', textAlign: 'center' }}><span style={{ fontSize: '12px', color: '#6c757d' }}>{affiliate.createdAt.toLocaleDateString()}</span></td>
                        <td style={{ padding: '15px', textAlign: 'center' }}>
                          <button onClick={async () => {
                            if (!window.confirm(canCreateLinks ? `Block ${affiliate.firstName}?` : `Unblock ${affiliate.firstName}?`)) return;
                            setProcessingAction(true);
                            try { await updateDoc(doc(db, 'affiliates', affiliate.id), { canCreateLinks: !canCreateLinks, linkCreationUpdatedAt: new Date(), linkCreationUpdatedBy: 'admin' }); }
                            catch (err) { alert(`Error: ${err.message}`); }
                            finally { setProcessingAction(false); }
                          }} disabled={processingAction} style={{ padding: '6px 16px', backgroundColor: canCreateLinks ? '#dc3545' : '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: processingAction ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                            {canCreateLinks ? '🚫 Block' : '✅ Unblock'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {affiliates.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>No affiliates yet</div>}
            </div>
          </div>
        )}

        {/* ── ANALYTICS ── */}
        {selectedTab === 'analytics' && (
          <div>
            <h2 style={{ marginBottom: '20px', color: '#495057' }}>Analytics & Reports</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginBottom: '20px' }}>Withdrawal Summary</h3>
                {[['Total', withdrawals.length, '#212529'], ['Completed', withdrawals.filter(w => w.status === 'completed').length, '#28a745'], ['Pending', withdrawals.filter(w => w.status === 'pending').length, '#dc3545'], ['Total Amount', `$${withdrawals.reduce((s, w) => s + w.amount, 0).toFixed(2)}`, '#212529']].map(([label, value, color]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span>{label}:</span><strong style={{ color }}>{value}</strong>
                  </div>
                ))}
              </div>

              <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginBottom: '20px' }}>Bonus Photo Unlocks</h3>
                {[
                  ['Total Purchases', stats.totalPurchases, '#e67e22'],
                  ['Total Revenue', `$${(stats.totalPurchases * 1.99).toFixed(2)}`, '#28a745'],
                  ['Links with Purchases', Object.values(bonusUnlockCounts).filter(n => n > 0).length, '#007bff'],
                  ['Avg per Link', ratingLinks.length > 0 ? (stats.totalPurchases / ratingLinks.length).toFixed(1) : '0', '#6c757d'],
                ].map(([label, value, color]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span>{label}:</span><strong style={{ color }}>{value}</strong>
                  </div>
                ))}
              </div>

              <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginBottom: '20px' }}>Quick Export</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button onClick={() => {
                    const data = withdrawals.map(w => { const a = affiliates.find(x => x.id === w.userId); return { name: `${a?.firstName} ${a?.lastName}`, email: a?.email, amount: w.amount, status: w.status, requestedAt: w.requestedAt.toISOString() }; });
                    const csv  = [Object.keys(data[0] || {}).join(','), ...data.map(r => Object.values(r).join(','))].join('\n');
                    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                    const a    = document.createElement('a'); a.href = url; a.download = `withdrawals_${new Date().toISOString().split('T')[0]}.csv`; a.click(); URL.revokeObjectURL(url);
                  }} style={{ padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>📊 Export All Withdrawals</button>
                  <button onClick={() => {
                    const data = affiliates.map(a => ({ name: `${a.firstName} ${a.lastName}`, email: a.email, balance: a.balance || 0, totalEarnings: a.totalEarnings || 0, totalRatings: a.totalRatings || 0, joinedAt: a.createdAt.toISOString() }));
                    const csv  = [Object.keys(data[0] || {}).join(','), ...data.map(r => Object.values(r).join(','))].join('\n');
                    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                    const a    = document.createElement('a'); a.href = url; a.download = `affiliates_${new Date().toISOString().split('T')[0]}.csv`; a.click(); URL.revokeObjectURL(url);
                  }} style={{ padding: '10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>👥 Export All Affiliates</button>
                </div>
              </div>

              <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginBottom: '20px' }}>Daily Stats</h3>
                {[
                  ["Today's Requests", stats.todayRequests],
                  ["Today's Amount", `$${withdrawals.filter(w => w.requestedAt.toDateString() === new Date().toDateString()).reduce((s, w) => s + w.amount, 0).toFixed(2)}`],
                  ['Avg Withdrawal', `$${withdrawals.length > 0 ? (withdrawals.reduce((s, w) => s + w.amount, 0) / withdrawals.length).toFixed(2) : '0.00'}`],
                  ['🔒 Encrypted', `${withdrawals.filter(w => w.encryptedBankAccount).length} / ${withdrawals.length}`]
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}><span>{label}:</span><strong>{value}</strong></div>
                ))}
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginTop: '20px' }}>
              <h3 style={{ marginBottom: '20px' }}>💰 Affiliate Pricing Control</h3>
              <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <AffiliatePricingControl />
              </div>
              <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '8px', padding: '15px', fontSize: '14px', color: '#856404' }}>
                <strong>⚠️ Note:</strong> Changes take effect immediately for all users.
              </div>
            </div>
          </div>
        )}
      </div>

      {processingAction && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '10px', textAlign: 'center' }}>
            <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #007bff', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 15px' }} />
            <p style={{ margin: 0, fontSize: '16px' }}>Processing...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;